import React, {
    useState,
    useCallback,
    useMemo,
    useEffect,
} from 'react';
import { observer } from 'mobx-react';
import { Button, Steps, message } from 'antd';
import {
    ArrowLeftOutlined,
    ArrowRightOutlined,
    CheckOutlined,
    DownloadOutlined,
    ExportOutlined,
} from '@ant-design/icons';

import { Input } from '@/components/Common/UIKit';
import { useStores } from '@/components/hooks';
import i18n from '@/content';
import { PUBLIC_ASSETS } from '@/consts';

import styles from './SharePointInlineWizard.module.scss';

const { Step } = Steps;

const AZURE_PORTAL_URL = 'https://portal.azure.com/#view/Microsoft_AAD_RegisteredApps/ApplicationsListBlade';
const NAMESPACE = 'settings.sharepointWizard';

export interface SharePointCredentials {
    site: string;
    clientId: string;
    realm: string;
}

export interface SharePointInlineWizardProps {
    onComplete: (credentials: SharePointCredentials) => void;
    onBack: () => void;
    organizationId?: string;
    defaultValues?: SharePointCredentials;
}

interface WizardStep {
    title: string;
}

function downloadCertificate(publicCertificate: string): void {
    const blob = new Blob([publicCertificate], { type: 'application/x-pem-file' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'specterx-sharepoint-cert.pem';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

const SharePointInlineWizard: React.FC<SharePointInlineWizardProps> = observer(({
    onComplete,
    onBack,
    organizationId,
    defaultValues,
}) => {
    const { settingsStore } = useStores();
    const {
        sharepointKeysStatus,
        isSharepointKeysLoading,
        isSharepointKeysGenerating,
        fetchSharepointKeysStatus,
        generateSharepointKeys,
    } = settingsStore;

    const [currentStep, setCurrentStep] = useState(0);
    const [generatedCertificate, setGeneratedCertificate] = useState<string | null>(null);
    const [credentials, setCredentials] = useState<SharePointCredentials>({
        site: defaultValues?.site ?? '',
        clientId: defaultValues?.clientId ?? '',
        realm: defaultValues?.realm ?? '',
    });

    useEffect(() => {
        fetchSharepointKeysStatus(organizationId);
    }, [fetchSharepointKeysStatus, organizationId]);

    useEffect(() => {
        if (defaultValues) {
            setCredentials(defaultValues);
        }
    }, [defaultValues]);

    const activeCertificate = generatedCertificate
        ?? sharepointKeysStatus?.public_certificate
        ?? null;
    const isCertificateConfigured = sharepointKeysStatus?.configured || !!generatedCertificate;

    const steps: WizardStep[] = useMemo(() => [
        { title: i18n.t(`${NAMESPACE}.steps.certificate.title`) },
        { title: i18n.t(`${NAMESPACE}.steps.azure.title`) },
        { title: i18n.t(`${NAMESPACE}.steps.configuration.title`) },
    ], []);

    const handleNext = useCallback((): void => {
        if (currentStep < steps.length - 1) {
            setCurrentStep((prev) => prev + 1);
        }
    }, [currentStep, steps.length]);

    const handlePrev = useCallback((): void => {
        if (currentStep > 0) {
            setCurrentStep((prev) => prev - 1);
        }
    }, [currentStep]);

    const handleCredentialChange = useCallback((field: keyof SharePointCredentials, value: string): void => {
        setCredentials((prev) => ({
            ...prev,
            [field]: value,
        }));
    }, []);

    const handleGenerate = useCallback(async (): Promise<void> => {
        const certificate = await generateSharepointKeys(organizationId);
        if (certificate) {
            setGeneratedCertificate(certificate);
            message.success(i18n.t(`${NAMESPACE}.certGenerated`));
        } else {
            message.error(i18n.t(`${NAMESPACE}.certGenerateFailed`));
        }
    }, [generateSharepointKeys, organizationId]);

    const handleDownload = useCallback((): void => {
        if (!activeCertificate) return;
        downloadCertificate(activeCertificate);
        message.success(i18n.t(`${NAMESPACE}.certDownloaded`));
    }, [activeCertificate]);

    const handleComplete = useCallback((): void => {
        onComplete(credentials);
    }, [credentials, onComplete]);

    const isCredentialsValid = useMemo((): boolean => (
        credentials.site.trim() !== ''
        && credentials.clientId.trim() !== ''
        && credentials.realm.trim() !== ''
    ), [credentials]);

    const renderCertificateStep = (): React.ReactNode => (
        <div className={styles.stepContent}>
            <p className={styles.stepDescription}>
                {i18n.t(`${NAMESPACE}.steps.certificate.intro`)}
            </p>
            {isCertificateConfigured && (
                <p className={styles.stepDescription}>
                    <strong>{i18n.t(`${NAMESPACE}.certReady`)}</strong>
                    {' '}
                    {i18n.t(`${NAMESPACE}.regenerateWarning`)}
                </p>
            )}
            <div className={styles.actionButtons}>
                <Button
                    type="primary"
                    onClick={handleGenerate}
                    loading={isSharepointKeysGenerating}
                    disabled={isSharepointKeysLoading}
                    size="large"
                >
                    {isCertificateConfigured
                        ? i18n.t(`${NAMESPACE}.regenerateCert`)
                        : i18n.t(`${NAMESPACE}.generateCert`)}
                </Button>
                {activeCertificate && (
                    <Button
                        icon={<DownloadOutlined />}
                        onClick={handleDownload}
                        size="large"
                    >
                        {i18n.t(`${NAMESPACE}.downloadCert`)}
                    </Button>
                )}
            </div>
            {activeCertificate && (
                <div className={styles.certificateBlock}>
                    <div className={styles.certificateHeader}>
                        <span>specterx-sharepoint-cert.pem</span>
                    </div>
                    <pre className={styles.certificatePre}>
                        <code>{activeCertificate}</code>
                    </pre>
                </div>
            )}
        </div>
    );

    const renderAzureStep = (): React.ReactNode => {
        const instructionSteps = [
            {
                title: i18n.t(`${NAMESPACE}.steps.azure.step1Title`),
                body: i18n.t(`${NAMESPACE}.steps.azure.step1Body`),
            },
            {
                title: i18n.t(`${NAMESPACE}.steps.azure.step2Title`),
                body: i18n.t(`${NAMESPACE}.steps.azure.step2Body`),
            },
            {
                title: i18n.t(`${NAMESPACE}.steps.azure.step3Title`),
                body: i18n.t(`${NAMESPACE}.steps.azure.step3Body`),
            },
            {
                title: i18n.t(`${NAMESPACE}.steps.azure.step4Title`),
                body: i18n.t(`${NAMESPACE}.steps.azure.step4Body`),
            },
        ];

        return (
            <div className={styles.stepContent}>
                <Button
                    type="primary"
                    icon={<ExportOutlined />}
                    href={AZURE_PORTAL_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="large"
                    className={styles.externalButton}
                >
                    {i18n.t(`${NAMESPACE}.openAzure`)}
                </Button>
                <ol className={styles.instructionsList}>
                    {instructionSteps.map((step) => (
                        <li key={step.title} className={styles.instructionItem}>
                            <strong>{step.title}</strong>
                            <p>{step.body}</p>
                        </li>
                    ))}
                </ol>
            </div>
        );
    };

    const renderConfigurationStep = (): React.ReactNode => (
        <div className={styles.stepContent}>
            <p className={styles.stepDescription}>
                {i18n.t(`${NAMESPACE}.steps.configuration.intro`)}
            </p>
            <div className={styles.credentialsForm}>
                <div className={styles.fieldRow}>
                    <label className={styles.fieldLabel} htmlFor="sharepoint-site">
                        {i18n.t('settings.sharepoint.site')}
                    </label>
                    <Input
                        id="sharepoint-site"
                        value={credentials.site}
                        onChange={(e) => handleCredentialChange('site', e.target.value)}
                        placeholder={i18n.t(`${NAMESPACE}.placeholders.site`)}
                        size="large"
                    />
                </div>
                <div className={styles.fieldRow}>
                    <label className={styles.fieldLabel} htmlFor="sharepoint-clientId">
                        {i18n.t('settings.sharepoint.clientId')}
                    </label>
                    <Input
                        id="sharepoint-clientId"
                        value={credentials.clientId}
                        onChange={(e) => handleCredentialChange('clientId', e.target.value)}
                        placeholder={i18n.t(`${NAMESPACE}.placeholders.clientId`)}
                        size="large"
                    />
                </div>
                <div className={styles.fieldRow}>
                    <label className={styles.fieldLabel} htmlFor="sharepoint-realm">
                        {i18n.t('settings.sharepoint.realm')}
                    </label>
                    <Input
                        id="sharepoint-realm"
                        value={credentials.realm}
                        onChange={(e) => handleCredentialChange('realm', e.target.value)}
                        placeholder={i18n.t(`${NAMESPACE}.placeholders.realm`)}
                        size="large"
                    />
                </div>
            </div>
        </div>
    );

    const renderStepContent = (): React.ReactNode => {
        switch (currentStep) {
            case 0:
                return renderCertificateStep();
            case 1:
                return renderAzureStep();
            case 2:
                return renderConfigurationStep();
            default:
                return null;
        }
    };

    const isLastStep = currentStep === steps.length - 1;
    const canAdvanceFromCertificateStep = currentStep !== 0 || isCertificateConfigured;

    return (
        <div className={styles.wizard}>
            <div className={styles.header}>
                <div className={styles.headerContent}>
                    <img
                        src={`${PUBLIC_ASSETS}/icons/sharepoint-logo.svg`}
                        alt="SharePoint"
                        className={styles.logo}
                    />
                    <div className={styles.headerText}>
                        <h2 className={styles.title}>
                            {i18n.t(`${NAMESPACE}.title`)}
                        </h2>
                        <p className={styles.subtitle}>
                            {i18n.t(`${NAMESPACE}.subtitle`)}
                        </p>
                    </div>
                </div>
            </div>

            <Steps
                current={currentStep}
                className={styles.steps}
                size="small"
                labelPlacement="vertical"
            >
                {steps.map((step) => (
                    <Step key={step.title} title={step.title} />
                ))}
            </Steps>

            <div className={styles.content}>
                {renderStepContent()}
            </div>

            <div className={styles.footer}>
                <Button
                    onClick={currentStep === 0 ? onBack : handlePrev}
                    icon={<ArrowLeftOutlined />}
                    className={styles.navButton}
                >
                    {currentStep === 0
                        ? i18n.t(`${NAMESPACE}.cancel`)
                        : i18n.t(`${NAMESPACE}.back`)}
                </Button>
                <div className={styles.footerRight}>
                    {isLastStep ? (
                        <Button
                            type="primary"
                            onClick={handleComplete}
                            disabled={!isCredentialsValid}
                            icon={<CheckOutlined />}
                            className={styles.completeButton}
                        >
                            {i18n.t(`${NAMESPACE}.complete`)}
                        </Button>
                    ) : (
                        <Button
                            type="primary"
                            onClick={handleNext}
                            disabled={!canAdvanceFromCertificateStep}
                            className={styles.nextButton}
                        >
                            {i18n.t(`${NAMESPACE}.next`)}
                            <ArrowRightOutlined />
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
});

export default SharePointInlineWizard;
